import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, buildWhatsAppLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { useI18n, formatDateLocale } from "@/lib/i18n";
import TicketScandyWidget from "@/components/TicketScandyWidget";

export default function EventDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ whatsapp_number: "34638382033" });
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setEvent(false)).finally(() => setLoading(false));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C544F]" data-testid="event-detail-loading">…</div>;
  }
  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="event-not-found">
        <p className="font-display text-4xl mb-4">404</p>
        <Link to="/" className="text-[#3e664c] underline">← {t.detail.back}</Link>
      </div>
    );
  }

  const onBuy = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t.contact.required); return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name, email: form.email, phone: form.phone,
        message: `Compra de ${form.quantity} entrada(s) para "${event.title}" (${event.date}).`,
        event_date: event.date,
      });
      const text = `Hola Aura, vull comprar ${form.quantity} entrada(es) per "${event.title}" del ${formatDateLocale(event.date, locale)}. ${form.name} · ${form.email} · ${form.phone}.`;
      window.open(buildWhatsAppLink(config.whatsapp_number, text), "_blank");
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", quantity: 1 });
    } catch {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  const total = event.ticket_price ? event.ticket_price * form.quantity : null;

  return (
    <div className="min-h-screen bg-[#fdfcf9] text-[#2A2421]" data-testid="event-detail-page">
      <div className="relative min-h-[60vh] md:min-h-[75vh] flex items-center justify-center overflow-hidden bg-[#1c2421]">
        {event.cover_video_url ? (
          <video
            src={event.cover_video_url}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full max-h-[85vh] object-contain"
            data-testid="event-cover-video"
          />
        ) : event.image_url ? (
          <img src={event.image_url} alt={event.title} className="w-full h-full max-h-[85vh] object-contain" />
        ) : null}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#fdfcf9] pointer-events-none" />
        <Link
          to="/" data-testid="event-back-link"
          className="absolute top-6 left-6 md:top-10 md:left-12 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fdfcf9]/90 backdrop-blur text-[#2A2421] text-sm hover:bg-[#fdfcf9]"
        >
          <ArrowLeft size={14} /> {t.detail.back}
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 -mt-32 md:-mt-44 relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }} className="lg:col-span-2"
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4" data-testid="event-date">
            {formatDateLocale(event.date, locale)} {event.time && `· ${event.time}h`}
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="event-title">
            {event.title}
          </h1>
          {event.description && (
            <p className="mt-8 text-[#2A2421]/80 text-lg leading-relaxed max-w-2xl" data-testid="event-description">
              {event.description}
            </p>
          )}
          {event.info_image_url && (
            <div className="mt-8 rounded-2xl overflow-hidden shadow-[0_20px_45px_rgb(28,36,33,0.12)] max-w-xl">
              <img
                src={event.info_image_url}
                alt={`${event.title} — info`}
                loading="lazy"
                className="w-full h-auto block"
                data-testid="event-info-image"
              />
            </div>
          )}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-2xl">
            <div>
              <p className="uppercase tracking-[0.2em] text-[10px] text-[#3e664c] mb-1">{t.detail.type}</p>
              <p className="font-medium">{t.detail.typeValue}</p>
            </div>
            <div>
              <p className="uppercase tracking-[0.2em] text-[10px] text-[#3e664c] mb-1">{t.detail.place}</p>
              <p className="font-medium">{t.detail.placeValue}</p>
            </div>
            {event.ticket_price && (
              <div>
                <p className="uppercase tracking-[0.2em] text-[10px] text-[#3e664c] mb-1">{t.detail.ticket}</p>
                <p className="font-medium" data-testid="event-price">{event.ticket_price.toFixed(2)} €</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="h-fit"
          data-testid="event-buy-panel"
        >
          {event.ticket_widget_id ? (
            <div className="bg-white rounded-3xl border border-[#eae0df] p-4 md:p-5 shadow-[0_18px_45px_rgb(42,36,33,0.08)]">
              <p className="uppercase tracking-[0.22em] text-[10px] text-[#3e664c] mb-2 px-2">{t.detail.tickets}</p>
              <h3 className="font-display text-2xl tracking-tight mb-4 px-2">{t.detail.reserve}</h3>
              <TicketScandyWidget widgetId={event.ticket_widget_id} widgetType={event.ticket_widget_type || "embed"} />
            </div>
          ) : (
            <form
              onSubmit={onBuy}
              className="bg-white rounded-3xl border border-[#eae0df] p-7 shadow-[0_18px_45px_rgb(42,36,33,0.08)]"
              data-testid="event-buy-form"
            >
              <p className="uppercase tracking-[0.22em] text-[10px] text-[#3e664c] mb-2">{t.detail.tickets}</p>
              <h3 className="font-display text-2xl tracking-tight mb-5">{t.detail.reserve}</h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="b-name" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelName}</Label>
                  <Input id="b-name" data-testid="buy-input-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 bg-white border-[#eae0df]" required />
                </div>
                <div>
                  <Label htmlFor="b-email" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelEmail}</Label>
                  <Input id="b-email" type="email" data-testid="buy-input-email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 bg-white border-[#eae0df]" required />
                </div>
                <div>
                  <Label htmlFor="b-phone" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelPhone}</Label>
                  <Input id="b-phone" data-testid="buy-input-phone" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 bg-white border-[#eae0df]" required />
                </div>
                <div>
                  <Label htmlFor="b-qty" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.detail.qty}</Label>
                  <Input id="b-qty" type="number" min={1} max={20} data-testid="buy-input-qty" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                    className="mt-1 bg-white border-[#eae0df]" />
                </div>
                {total !== null && (
                  <div className="flex items-center justify-between border-t border-[#eae0df] pt-4 text-sm">
                    <span className="uppercase tracking-[0.18em] text-[#5C544F]">{t.detail.total}</span>
                    <span className="font-display text-2xl" data-testid="buy-total">{total.toFixed(2)} €</span>
                  </div>
                )}
                <Button type="submit" data-testid="buy-submit" disabled={submitting}
                  className="w-full rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] py-6 text-sm uppercase tracking-[0.18em]">
                  {submitting ? t.detail.buying : t.detail.buy}
                </Button>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C544F]/70 text-center">
                  {t.detail.footnote}
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </div>

      <div className="h-32" />
      <WhatsAppFloat number={config.whatsapp_number} />
    </div>
  );
}
